import { createApiInstance } from "./apiClient";

// Public, anonymous-capable multi-step trade-account application (backend: api/cod). A draft is started
// (returning a reference number + one-time resume token), saved incrementally, has documents attached, and is
// later submitted. Access is by resume token (anonymous) or ownership (authenticated).
//
// Two contract details are handled here so callers never see them:
//  - CAPTCHA: the one-time token from <Captcha/> is sent as the X-Captcha-Token header on start (the abuse-prone,
//    email-amplification surface). The API verifies it server-side; callers just pass the opaque token through.
//  - Resume token: sent as the X-Resume-Token header, NOT the ?token query, so this PII-bearing token
//    stays out of URLs / access logs / browser history.
const url = process.env.REACT_APP_ORDER_API_URL;
const CAPTCHA_TOKEN_HEADER = "X-Captcha-Token";
const RESUME_TOKEN_HEADER = "X-Resume-Token";

// The document slots the API assigns an uploaded submission to. Exported so callers avoid magic numbers.
// DirectorIdCopy / DirectorSuretySignature require a directorKey.
export const ApplicationDocumentSlot = Object.freeze({
  VatRegistration: 1,
  DeedOfSuretySignature: 2,
  DirectorIdCopy: 3,
  DirectorSuretySignature: 4,
});

// Media type IDs — mirrors the backend MediaType enum. Exported so callers reference names, not magic numbers.
export const ApplicationMediaType = Object.freeze({
  Image: 1,      // photos, image scans
  Video: 2,
  Document: 3,   // PDF documents (VAT cert, ID copy, etc.)
  Signature: 4,  // canvas-drawn PNG signatures
});

const codInstance = ({ resumeToken, captchaToken } = {}) => {
  const headers = {};
  if (captchaToken != null) headers[CAPTCHA_TOKEN_HEADER] = captchaToken;
  if (resumeToken) headers[RESUME_TOKEN_HEADER] = resumeToken;
  return createApiInstance({ baseURL: `${url}/api/cod`, retry: true, headers });
};

const asResult = (response) => {
  const ok =
    response.status === 200 ||
    response.status === 201 ||
    response.status === 204;
  return ok
    ? {
        error: false,
        success: true,
        message: response.data?.message,
        data: response.data?.data,
      }
    : { error: true, success: false, message: response.data?.message };
};

// A 400 body from these endpoints is a validation dictionary — { field: [messages] } (FluentValidation's
// ToDictionary), or a ProblemDetails carrying an `errors` map. Parse it so the field errors AND a readable
// summary reach the FE instead of being swallowed behind a generic fallback.
const parseValidationErrors = (data) => {
  if (data && Array.isArray(data.errors)) {
    const problemMessages = data.errors.map((e) => e?.message).filter(Boolean);
    return {
      errors: null,
      message: problemMessages.length ? problemMessages.join(" ") : data.title || null,
    };
  }

  const dict =
    data &&
    typeof data === "object" &&
    data.errors &&
    typeof data.errors === "object"
      ? data.errors
      : data;
  if (!dict || typeof dict !== "object") return { errors: null, message: null };
  const messages = Object.values(dict)
    .flatMap((v) => (Array.isArray(v) ? v : []))
    .filter(Boolean);
  return {
    errors: messages.length ? dict : null,
    message: messages.length
      ? messages.join(" ")
      : data.title || data.message || null,
  };
};

// The backend maps failed Results to normalized status codes: 400 invalid, 403 forbidden,
// 404 not-found, 409 conflict. axios throws on 4xx, so we translate the code into flags the PageFunction can
// use to message precisely (e.g. 409 -> "already submitted", 403/404 -> "resume link invalid or expired"),
// and we surface the 400 validation errors (field map + summary) rather than swallowing them.
const failureFromError = (error, fallbackMessage) => {
  if (error.response) {
    console.error("Server Error:", error.response.status);
    console.error("Server Error Body:", error.response.data);
  } else if (error.request) {
    console.error("Network Error:", error.request);
  } else {
    console.error("Error:", error.message);
  }
  const status = error?.response?.status;
  const { errors, message } = parseValidationErrors(error?.response?.data);
  return {
    error: true,
    success: false,
    status,
    notFound: status === 404,
    invalid: status === 400,
    conflict: status === 409,
    forbidden: status === 403,
    errors, // { field: [messages] } for per-field display; null when not a validation error
    message: message || fallbackMessage,
  };
};

// POST api/cod — start a draft. `captchaToken` is the opaque value from <Captcha/>; it becomes X-Captcha-Token.
// On success `data` is { applicationId, referenceNumber, resumeToken, statusId } — resumeToken is shown ONCE.
export const startTradeApplication = async ({ request, captchaToken }) => {
  try {
    const response = await codInstance({ captchaToken }).post(``, request);
    return asResult(response);
  } catch (error) {
    return failureFromError(error, "Could not start the application.");
  }
};

// GET api/cod/{reference} — load a draft (or a submitted application) by reference + resume token.
export const getTradeApplication = async ({ reference, resumeToken }) => {
  try {
    const response = await codInstance({ resumeToken }).get(`/${reference}`);
    return asResult(response);
  } catch (error) {
    return failureFromError(error, "Could not load the application.");
  }
};

// PUT api/cod/{reference} — save partial progress (204). `request` is an UpsertTradeApplicationRequest.
export const saveTradeApplicationDraft = async ({
  reference,
  resumeToken,
  request,
}) => {
  try {
    const response = await codInstance({ resumeToken }).put(
      `/${reference}`,
      request,
    );
    return asResult(response);
  } catch (error) {
    return failureFromError(error, "Could not save the application.");
  }
};

// POST api/cod/{reference}/documents — attach an already-uploaded submission to a typed slot (204).
// `submissionId` comes from uploadEntityMediaFile; `directorKey` is required for the director slots.
export const assignTradeApplicationDocument = async ({
  reference,
  resumeToken,
  slot,
  submissionId,
  directorKey = null,
}) => {
  try {
    const body = { slot, submissionId };
    if (directorKey != null) body.directorKey = directorKey;
    const response = await codInstance({ resumeToken }).post(
      `/${reference}/documents`,
      body,
    );
    return asResult(response);
  } catch (error) {
    return failureFromError(error, "Could not attach the document.");
  }
};

// POST api/cod/{reference}/submit — submit the completed application (204). 409 => already submitted.
export const submitTradeApplication = async ({ reference, resumeToken }) => {
  try {
    const response = await codInstance({ resumeToken }).post(
      `/${reference}/submit`,
    );
    return asResult(response);
  } catch (error) {
    return failureFromError(error, "Could not submit the application.");
  }
};
